// Catch-all library for puzzle-related things that classes like
// BoardInteractionContext and PlayInteractionContext can pull from.

import { ACROSS, DOWN, emptyCharGrid, PuzzleMetadataKey, SlotStructure, SymmetryType } from "./directionsLib";
import { emptyFurnishingsObject } from "./furnishingsLib";




// Define initial values once to avoid re-creating them multiple times
export const DEFAULT_CHAR_GRID = emptyCharGrid(7, 7);
export const DEFAULT_SLOT_STRUCTURE = SlotStructure.buildNewSlotStructure(DEFAULT_CHAR_GRID);
export const DEFAULT_FURNISHINGS = emptyFurnishingsObject();
export const DEFAULT_CLUES = new Map(DEFAULT_SLOT_STRUCTURE.slotNames.map(slotName => [slotName, '']));
export const DEFAULT_PUZZLE_METADATA = new Map([
  [PuzzleMetadataKey.TITLE, ''],
  [PuzzleMetadataKey.AUTHOR, ''],
  [PuzzleMetadataKey.EDITOR, ''],
  [PuzzleMetadataKey.COPYRIGHT, ''],
  [PuzzleMetadataKey.PUBLISHER, ''],
  [PuzzleMetadataKey.DATE, ''],
  [PuzzleMetadataKey.START_MESSAGE, ''],
  [PuzzleMetadataKey.END_MESSAGE, ''],
  [PuzzleMetadataKey.SYMMETRY_TYPE, SymmetryType.ROTATIONAL],
]);



const ANY_CLUE_COMMAND_PATTERN = /(?:link:\d+[AD]|hide)/;
const ANY_CLUE_COMMAND_PATTERN_FULL_STRING = new RegExp(`^${ANY_CLUE_COMMAND_PATTERN.source}$`, ANY_CLUE_COMMAND_PATTERN.flags);



export function condenseSlotName(slotName) {
  return slotName.replace(/(\d+)-(\w)\w+/, (_, n, d) => n + d.toUpperCase());
}

export function expandSlotName(condensed) {
  return condensed.replace(/(\d+)([AD])/, (_, n, d) => `${n}-${d === 'A' ? ACROSS : DOWN}`);
}



/**
 * Returns a list of strings that are the clueCommands contained by the clue.
 * Clue commands are substrings within the clue text bookended by $ symbols (and not containing any internal $ symbols),
 * and may be chained together (e.g. "This is my clue text $link:4A$link:8A$") - although this is mostly for legacy support, and probably won't happen in reality.
 * This function can also filter the extracted clueCommands and return the ones matching a specified pattern.
 * If given, the internalPattern should use the ^ and $ tokens if it's meant to match the whole clue string.
 * @param {RegExp?} internalPattern optional RegExp specifying the internal pattern to filter for (i.e. the command pattern excluding the start and end $ symbols).
 */
export function extractClueCommands(clueText, internalPattern = ANY_CLUE_COMMAND_PATTERN_FULL_STRING) {
  const clueCommands = [];
  const tokens = (clueText || '').split('$');
  for (let i = 1; i < tokens.length - 1; ++i) {   // skip first and last tokens, as they are definitionally not clue commands
    const token = tokens[i];
    if (internalPattern.test(token) && !clueCommands.includes(token)) {
      clueCommands.push(token);
    }
  }
  return clueCommands;
}


/**
 * Removes a specific clue command from a clue text string and returns the new string. Takes into account whether the $ is part of anonther recognized clue command.
 * @param {string} clueText 
 * @param {string} clueCommand the clue command as the exact string WITHOUT the bookending $ symbols
 * @returns {string} the clue text without the specific clue command. The bookending $ are only preserved if they are a part of another recognized clue command.
 */
export function removeClueCommand(clueText, clueCommand) {
  // Split by $ delineator
  const parts = clueText.split('$');
  const dollaSigns = new Array(parts.length - 1).fill('$');

  // First, check $ signs, the only way we remove a $ is if either the preceding or subsequent substring matches the clueCommand, AND the substring on the other side is not any recognized clue command.
  for (let i = 0; i < dollaSigns.length; ++i) {
    const prevSubstr = parts[i];
    const nextSubstr = parts[i+1];
    if ( (prevSubstr === clueCommand && i > 0 && !ANY_CLUE_COMMAND_PATTERN_FULL_STRING.test(nextSubstr)) || (nextSubstr === clueCommand && i < dollaSigns.length - 1 && !ANY_CLUE_COMMAND_PATTERN_FULL_STRING.test(prevSubstr)) ) {
      dollaSigns[i] = '';   // remove $
    }
  }

  // Second, remove the actual substring(s) that match the given clueCommand
  for (let i = 1; i < parts.length - 1; ++i) {   // first and last elements cannot be clue commands
    if (parts[i] === clueCommand) {
      parts[i] = '';
    }
  }

  // Finally, reassemble all the remaining parts and dolla $igns
  let res = parts[0];
  for (let i = 0; i < dollaSigns.length; ++i) {
    res += dollaSigns[i];
    res += parts[i+1];
  }
  return res;
}


/**
 * Returns a string without the recognized clue command pattern.
 * @param {string} clueText 
 * @param {RegExp} pattern The RegExp pattern INSIDE the $ symbols that should be matched and removed (the $ will also be removed, but should not be included in the RegExp)
 * @returns {string} the clueText without the recognized RegExp and its bookending $'s
 */
export function removeClueCommands(clueText, pattern=ANY_CLUE_COMMAND_PATTERN) {
  // We want to mark all the matching indexes in the string, including the bookending $'s
  const patternWithBookends = new RegExp('(?=(\\$' + pattern.source + '\\$))', 'gd');

  const indexes = [];
  let match;
  while ((match = patternWithBookends.exec(clueText)) !== null) {
    for (let i = match.indices[1][0]; i < match.indices[1][1]; ++i) {
      if (!indexes.includes(i)) indexes.push(i);
    }
    // Reset regex lastIndex to start from the next character after the current match
    patternWithBookends.lastIndex = match.index + 1;
  }

  // Remove all the characters at the specified indexes
  if (indexes.length === 0) {
    return clueText;
  }

  const charArray = clueText.split('');
  indexes.sort((a, b) => b - a);   // use descending order to avoid shifting problems
  for (const idx of indexes) {
    if (idx >= 0 && idx < charArray.length) {
      charArray.splice(idx, 1);
    }
  }
  return charArray.join('');
}


